import { useState } from 'react';
import { DmsModal, DmsButton, DmsCheckBox, DmsAlertWarning, intl } from 'dmsbasereact';
import { Box, Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/**
 * Modal to alert when prescription quantity is above package insert recommendations
 * @param {Object} props - Component properties
 * @param {boolean} props.isVisible - Controls modal visibility
 * @param {function} props.onClose - Callback to close modal (cancel)
 * @param {function} props.onConfirm - Callback to confirm and proceed
 * @param {string} props.dosageDescription - Description of the prescribed dosage
 * @returns {JSX.Element} Quantity alert modal component
 */
const PrescriptionQuantityAlertModal = ({
  isVisible,
  onClose,
  onConfirm,
  dosageDescription = ''
}) => {
  const [confirmationChecked, setConfirmationChecked] = useState(false);

  const handleClose = () => {
    setConfirmationChecked(false);
    onClose();
  };

  const handleConfirm = () => {
    if (confirmationChecked) {
      setConfirmationChecked(false);
      onConfirm();
    }
  };

  return (
    <DmsModal isVisible={isVisible} onClose={handleClose} maxWidth="sm" fullWidth>
      <DmsModal.Header
        title={intl.get("QuantityAbovePackageInsert").d("Warning: Quantity Above Package Insert")}
        onClose={handleClose}
      />
      <DmsModal.Body>
        <Box sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
            <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 40, mt: 0.5 }} />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {intl.get("QuantityAbovePackageInsertMessage").d(
                  "The prescribed quantity is above the recommended dosage in the package insert."
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {intl.get("QuantityAbovePackageInsertWarning").d(
                  "Please verify that the prescribed dosage is correct and intended for this patient."
                )}
              </Typography>
            </Box>
          </Box>

          {dosageDescription && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {intl.get("prescribed_dosage").d("Prescribed Dosage")}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                {dosageDescription}
              </Typography>
            </Box>
          )}

          <DmsAlertWarning
            sx={{ mb: 2 }}
            message={intl.get("QuantityAbovePackageInsertResponsibility").d(
              "By confirming, you assume full responsibility for prescribing a quantity above the package insert recommendations."
            )}
          />

          <Box sx={{ mt: 3 }}>
            <DmsCheckBox
              label={intl.get("ConfirmQuantityAbovePackageInsert").d(
                "I confirm that I want to proceed with this quantity"
              )}
              labelComponent={(props) => <Typography variant="body2" {...props} />}
              checked={confirmationChecked}
              onChange={(e) => setConfirmationChecked(e.target.checked)}
              name="confirmQuantityAbovePackageInsert"
            />
          </Box>
        </Box>
      </DmsModal.Body>
      <DmsModal.Footer>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <DmsButton
              variant="secondary"
              text={intl.get("Cancel").d("Cancel")}
              onClick={handleClose}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <DmsButton
              variant="primary"
              text={intl.get("confirm").d("Confirm")}
              onClick={handleConfirm}
              fullWidth
              disabled={!confirmationChecked}
            />
          </Grid>
        </Grid>
      </DmsModal.Footer>
    </DmsModal>
  );
};

PrescriptionQuantityAlertModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  dosageDescription: PropTypes.string
};


export default PrescriptionQuantityAlertModal;
